/**
 * importSummary — the numbers at the top of the import review.
 *
 * The import used to close with its own summary SCREEN, ahead of the review:
 * two tiles (items added, vendors) and a vendor breakdown, dismissed by a Next
 * button. It was a second place for the same import to state its results, and
 * the two places disagreed — the screen said "127 items added, 9 vendors" while
 * the review underneath it said nineteen of those items "may already be an item
 * already in your catalog", in a store whose catalog was empty. The counts now
 * live on the review itself, derived from the very rows the reviewer is looking
 * at, so there is one account of what happened and it is attached to the
 * evidence for it.
 *
 * The load-bearing distinction here is WHO a matched row was matched to:
 *
 *   related to the catalog   the partner item predates this import session —
 *                            the store already stocked this product
 *   related within the import the partner is another line from this same
 *                            import — one product, pasted twice
 *
 * They are the same mechanism and completely different news. A first import
 * into a brand-new store can only produce the second kind, and reporting it as
 * the first told the user their empty catalog already held the products.
 *
 * Pure and DOM-free: the React review header and its tests share one model with
 * the relationships section, so the tiles can never contradict the cards.
 */

import { summarizeLandings, LANDING } from './importLanding';

/** "vendor · pack · $price" from whichever of those facts the record states. */
export function relationshipDetails({ vendor, pack, price, unitPrice, unit } = {}) {
  const parts = [];
  const vendorText = String(vendor ?? '').trim();
  const packText = String(pack ?? '').trim();
  if (vendorText) parts.push(vendorText);
  if (packText) parts.push(packText);
  const packagePrice = parseFloat(price);
  const perUnit = parseFloat(unitPrice);
  if (Number.isFinite(packagePrice) && packagePrice > 0) parts.push(`$${packagePrice.toFixed(2)}`);
  else if (Number.isFinite(perUnit) && perUnit > 0) parts.push(`$${perUnit.toFixed(2)}${unit ? `/${unit}` : ''}`);
  return parts.join(' · ');
}

export function matchPercent(score) {
  // Number(null) is 0 and Number('') is 0, and both are finite — so an
  // UNSCORED link rendered as "0% match", which reads as a model that looked
  // and found nothing in common. A deterministic link has no score because
  // none applies, not because it scored zero. (The same hazard is guarded 40
  // lines below in importOriginLookup; this is the one that got away, and the
  // vanilla twin in import-consolidation-review.js has always checked null
  // first.) Only a real number renders a percentage.
  if (score === null || score === undefined || score === '') return null;
  const n = Number(score);
  if (!Number.isFinite(n)) return null;
  return Math.round(Math.max(0, Math.min(1, n)) * 100);
}

/**
 * Which items THIS import session created, as a Set of numeric ids.
 *
 * Sources, strongest first:
 *   createdItemIds  the host's session-wide ledger (every file's outcomes),
 *                   the only source that spans a multi-file import
 *   the rows        each carries the provenance the server stamped on it
 *                   (`_possibleMatch.from_this_import`,
 *                   `_existingItemFromThisImport`), which covers one file
 *
 * A partner id in neither is reported as unknown rather than guessed at — see
 * `relatedUnknownOrigin`.
 */
export function importOriginLookup(items, createdItemIds) {
  const created = new Set(
    (createdItemIds || [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id)),
  );
  // id → boolean, read off whatever the rows themselves already state.
  const stamped = new Map();
  (items || []).forEach((item) => {
    const possible = item?._possibleMatch;
    if (possible && possible.id != null && typeof possible.from_this_import === 'boolean') {
      stamped.set(Number(possible.id), possible.from_this_import);
    }
    const existingId = item?._existingItem?.id;
    if (existingId != null && typeof item?._existingItemFromThisImport === 'boolean') {
      stamped.set(Number(existingId), item._existingItemFromThisImport);
    }
  });
  return (partnerId) => {
    // Number(null) is 0 and Number('') is 0 — both finite, and both would be
    // answered "predates the import" by the ledger check below. A partner we
    // cannot identify is unknown, which is a different answer.
    if (partnerId === null || partnerId === undefined || partnerId === '') return null;
    const id = Number(partnerId);
    if (!Number.isFinite(id)) return null;
    if (created.has(id)) return true;
    if (stamped.has(id)) return stamped.get(id);
    // A session ledger that names creations at all is a complete list of them,
    // so anything absent from it predates the import.
    if (created.size > 0) return false;
    return null;
  };
}

/**
 * One card per relationship, in one shape however the join is carried out —
 * mirrors public/js/import-consolidation-review.js#relationshipLinks (a pending
 * cross-reference row carries the existing side as suggestion.primary_item_*;
 * an already-written attach as group.existingItem).
 *
 *   kind 'suggestion' — pending, resolved by accepting/dismissing its row
 *   kind 'combined'   — already written, undone by detaching the product
 *   kind 'context'    — a partner we can name but cannot action (no row id)
 *
 * `originOf` (optional) is the lookup above; each link reports its partner's
 * provenance as `fromThisImport` (true / false / null when unknown).
 */
export function relationshipCardModel(group, originOf = () => null) {
  const raw = Array.isArray(group.suggestions) && group.suggestions.length > 0
    ? group.suggestions
    : [group.suggestion];
  const suggestions = raw.filter((entry) => entry && entry.primary_item_id != null);
  let links = [];
  if (suggestions.length > 0) {
    links = suggestions.map((suggestion) => ({
      kind: suggestion.id != null ? 'suggestion' : 'context',
      suggestionId: suggestion.id != null ? suggestion.id : null,
      partnerId: suggestion.primary_item_id,
      fromThisImport: originOf(suggestion.primary_item_id),
      name: String(suggestion.primary_item_name || 'an existing item'),
      details: relationshipDetails({
        vendor: suggestion.primary_item_vendor,
        pack: suggestion.primary_item_pack,
        price: suggestion.primary_item_package_price,
        unitPrice: suggestion.primary_item_unit_price,
        unit: suggestion.primary_item_unit,
      }),
      unit: suggestion.primary_item_unit ? String(suggestion.primary_item_unit) : null,
      pct: matchPercent(suggestion.score),
    }));
  } else if (group.action === 'attached' || group.action === 'merged') {
    // The surviving item IS the partner here: this row was folded onto it.
    links = [{
      kind: 'combined',
      suggestionId: null,
      partnerId: group.itemId,
      fromThisImport: originOf(group.itemId),
      name: String((group.existingItem && group.existingItem.name) || group.itemName),
      details: relationshipDetails({
        vendor: group.existingItem && group.existingItem.vendor,
        pack: group.existingItem && group.existingItem.pack,
        price: group.existingItem && group.existingItem.package_price,
        unitPrice: group.existingItem && group.existingItem.unit_price,
        unit: group.existingItem && group.existingItem.unit,
      }),
      unit: group.existingItem && group.existingItem.unit
        ? String(group.existingItem.unit)
        : null,
      pct: matchPercent(group.autoLinkScore),
    }];
  }
  const members = (Array.isArray(group.options) ? group.options : []).filter((o) => o && o.id != null);
  return {
    key: String(group.itemId),
    name: String(group.itemName || 'Item'),
    unit: group.unit ? String(group.unit) : null,
    members,
    links,
    // planChanges skips option unticks on standalone suggested groups (the
    // option already belongs to a standalone item), so offer member
    // checkboxes only where unticking actually detaches something.
    membersActionable: members.length >= 2 || links.some((l) => l.kind === 'combined'),
  };
}

/**
 * Fold relationship edges into the connected product groups a reviewer
 * actually thinks in. The save layer still owns every original suggestion;
 * this is presentation-only, so a lime connected through another lime renders
 * as one card without changing which relationship rows will be written.
 *
 * Products are also folded carefully: a partner may later appear as a source
 * card with the same vendor/pack/price evidence. In that case the real option
 * replaces the link snapshot in-place. Distinct purchase options remain
 * distinct even when they happen to share a price.
 *
 * Each product also carries what declining it would DO, so the review's
 * keep/decline controls can act without unfolding the group:
 *   suggestionIds     the pending suggestion rows that connect this product to
 *                     the group — declining dismisses them (folding merges a
 *                     dropped or replaced link snapshot's ids onto the product
 *                     that stands for it, so a folded partner stays declinable)
 *   optionActionable  true when unticking this vendor product is a real detach
 *                     (mirrors relationshipCardModel's membersActionable —
 *                     planChanges skips option unticks on standalone suggested
 *                     groups, so no control is offered there)
 * A product with neither is context only (a combined join already written, or
 * a partner with no actionable row) and gets no control.
 */
export function groupRelationshipCards(cards) {
  const source = (Array.isArray(cards) ? cards : []).filter(Boolean);
  if (source.length === 0) return [];

  const parent = new Map();
  const ensure = (key) => {
    if (!parent.has(key)) parent.set(key, key);
    return key;
  };
  const find = (key) => {
    let current = ensure(key);
    while (parent.get(current) !== current) current = parent.get(current);
    let cursor = key;
    while (parent.get(cursor) !== cursor) {
      const next = parent.get(cursor);
      parent.set(cursor, current);
      cursor = next;
    }
    return current;
  };
  const union = (a, b) => {
    const rootA = find(a);
    const rootB = find(b);
    if (rootA !== rootB) parent.set(rootB, rootA);
  };
  const itemNode = (id) => `item:${String(id)}`;

  source.forEach((card, cardIndex) => {
    const root = itemNode(card.key);
    ensure(root);
    (card.links || []).forEach((link, linkIndex) => {
      const partner = link?.partnerId === null || link?.partnerId === undefined || link?.partnerId === ''
        ? `anonymous:${cardIndex}:${linkIndex}`
        : itemNode(link.partnerId);
      union(root, partner);
    });
  });

  const groupByRoot = new Map();
  source.forEach((card) => {
    const componentRoot = find(itemNode(card.key));
    let group = groupByRoot.get(componentRoot);
    if (!group) {
      group = {
        key: `relationship-group:${String(card.key)}`,
        products: [],
        relationshipCount: 0,
        _cards: [],
      };
      groupByRoot.set(componentRoot, group);
    }
    group._cards.push(card);
  });

  const normalized = (value) => String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
  const sameLinkSnapshot = (left, right) => {
    if (String(left.inventoryItemId) !== String(right.inventoryItemId)) return false;
    const leftDetails = normalized(left.details);
    const rightDetails = normalized(right.details);
    if (leftDetails && rightDetails) return leftDetails === rightDetails;
    return normalized(left.name) === normalized(right.name);
  };

  const mergeSuggestionIds = (into, from) => {
    const seen = new Set((into || []).map(String));
    (from || []).forEach((id) => {
      if (id == null || seen.has(String(id))) return;
      seen.add(String(id));
      into.push(id);
    });
    return into;
  };

  for (const group of groupByRoot.values()) {
    const seenOptionIds = new Set();
    const seenRelationships = new Set();

    const addProduct = (product) => {
      if (product.optionId != null) {
        const optionKey = String(product.optionId);
        if (seenOptionIds.has(optionKey)) return;
        seenOptionIds.add(optionKey);
        const snapshotIndex = group.products.findIndex((existing) => (
          existing.source === 'link' && sameLinkSnapshot(existing, product)
        ));
        if (snapshotIndex >= 0) {
          // The real option replaces the link snapshot — the pending edges the
          // snapshot carried move with it, or the fold would eat the decline.
          mergeSuggestionIds(product.suggestionIds, group.products[snapshotIndex].suggestionIds);
          group.products[snapshotIndex] = product;
        } else group.products.push(product);
        return;
      }
      const existing = group.products.find((candidate) => sameLinkSnapshot(candidate, product));
      if (existing) {
        mergeSuggestionIds(existing.suggestionIds, product.suggestionIds);
        return;
      }
      group.products.push(product);
    };

    group._cards.forEach((card, cardIndex) => {
      const members = Array.isArray(card.members) ? card.members : [];
      if (members.length === 0) {
        addProduct({
          key: `item:${String(card.key)}`,
          inventoryItemId: card.key,
          optionId: null,
          optionActionable: false,
          suggestionIds: [],
          name: String(card.name || 'Item'),
          details: '',
          countUnit: card.unit ? String(card.unit) : null,
          source: 'item',
        });
      } else {
        members.forEach((option) => {
          addProduct({
            key: `option:${String(option.id)}`,
            inventoryItemId: card.key,
            optionId: option.id,
            optionActionable: card.membersActionable === true,
            suggestionIds: [],
            // A one-product source card previously printed the inventory name
            // as a card title and then a second vendor title immediately below
            // it. Keep the source-facing name once. Multi-product cards still
            // need each vendor's own title to tell their options apart.
            name: String(
              members.length === 1
                ? (card.name || option.vendor_item_name || option.vendor_name || 'Vendor product')
                : (option.vendor_item_name || option.vendor_name || card.name || 'Vendor product'),
            ),
            details: relationshipDetails({
              vendor: option.vendor_name,
              pack: option.pack_description,
              price: option.package_price,
              unitPrice: option.normalized_unit_cost,
              unit: option.purchase_unit,
            }),
            countUnit: card.unit ? String(card.unit) : null,
            source: 'option',
          });
        });
      }

      (card.links || []).forEach((link, linkIndex) => {
        const relationshipKey = link?.suggestionId != null
          ? `suggestion:${String(link.suggestionId)}`
          : `edge:${[String(card.key), String(link?.partnerId ?? `anonymous-${cardIndex}-${linkIndex}`)].sort().join(':')}:${String(link?.kind || '')}`;
        if (!seenRelationships.has(relationshipKey)) {
          seenRelationships.add(relationshipKey);
          group.relationshipCount += 1;
        }
        addProduct({
          key: `link:${String(card.key)}:${String(link?.partnerId ?? linkIndex)}:${linkIndex}`,
          inventoryItemId: link?.partnerId ?? `anonymous-${cardIndex}-${linkIndex}`,
          optionId: null,
          optionActionable: false,
          suggestionIds: link?.kind === 'suggestion' && link?.suggestionId != null
            ? [link.suggestionId]
            : [],
          name: String(link?.name || 'Related product'),
          details: String(link?.details || ''),
          countUnit: link?.unit ? String(link.unit) : null,
          source: 'link',
        });
      });

      if ((card.links || []).length === 0 && members.length >= 2) {
        const relationshipKey = `grouped:${String(card.key)}`;
        if (!seenRelationships.has(relationshipKey)) {
          seenRelationships.add(relationshipKey);
          group.relationshipCount += 1;
        }
      }
    });

    delete group._cards;
  }

  return [...groupByRoot.values()];
}

/**
 * Tally the relationships across the cards, split by partner provenance.
 *
 * Counted per LINK, deduplicated by pending-suggestion id, so an item offered
 * two partners counts twice and one proposal drawn on two cards counts once —
 * the same arithmetic RelationshipsSection does for its own header, from the
 * same card models.
 *
 * A card with no link but several members is several vendor products landing on
 * ONE new item: a relationship entirely inside this import, with no partner id
 * to look up, so it is counted as within-import rather than as unknown.
 */
export function tallyRelationships(cards) {
  let toExisting = 0;
  let withinImport = 0;
  let unknownOrigin = 0;
  const counted = new Set();
  (cards || []).forEach((card) => {
    if (!card) return;
    if (card.links.length === 0) {
      if (card.members.length >= 2) withinImport += 1;
      return;
    }
    card.links.forEach((link) => {
      if (link.suggestionId != null) {
        const id = String(link.suggestionId);
        if (counted.has(id)) return;
        counted.add(id);
      }
      if (link.fromThisImport === true) withinImport += 1;
      else if (link.fromThisImport === false) toExisting += 1;
      else unknownOrigin += 1;
    });
  });
  return { toExisting, withinImport, unknownOrigin, total: toExisting + withinImport + unknownOrigin };
}

/**
 * Everything the review header states, from the rows the reviewer is about to
 * approve plus the two facts they cannot carry themselves: which vendors the
 * store already had, and how many lines the source documents actually held.
 *
 * @param {Object}   input
 * @param {Array}    input.items              the review rows
 * @param {Function} [input.contextFor]       (row) => landing context
 * @param {Array}    [input.relationshipGroups] relatedReviewGroups output
 * @param {Array}    [input.createdItemIds]   ids this import session created
 * @param {Array}    [input.vendors]          every vendor named by the import
 * @param {Array}    [input.newVendors]       those the store did not have
 * @param {number}   [input.itemsFound]       lines found in the import data
 */
export function buildImportSummary({
  items = [],
  contextFor,
  relationshipGroups = [],
  createdItemIds = [],
  vendors = [],
  newVendors = [],
  itemsFound = null,
} = {}) {
  const landings = summarizeLandings(items, contextFor || (() => ({})));
  const originOf = importOriginLookup(items, createdItemIds);

  // Vendors named on the rows, unioned with the ones the extraction reported.
  // Either alone has been wrong: extraction returns none for some documents,
  // and a vendor whose every row was rejected still appears in the source.
  const vendorNames = new Set();
  (vendors || []).forEach((name) => {
    const trimmed = String(name ?? '').trim();
    if (trimmed) vendorNames.add(trimmed);
  });
  (items || []).forEach((item) => {
    const trimmed = String(item?.vendor ?? '').trim();
    if (trimmed) vendorNames.add(trimmed);
  });
  const newVendorNames = (newVendors || [])
    .map((name) => String(name ?? '').trim())
    .filter(Boolean);

  const cards = (relationshipGroups || [])
    .map((group) => relationshipCardModel(group, originOf))
    .filter((card) => card.links.length > 0 || card.members.length >= 2);
  const fromCards = tallyRelationships(cards);
  // Without relationship cards — the enrichment tail has not landed yet, or
  // this flow does not run one — the rows still carry their own verdicts.
  const related = fromCards.total > 0
    ? fromCards
    : {
      toExisting: landings.relationships.toExisting,
      withinImport: landings.relationships.withinImport,
      unknownOrigin: 0,
      total: landings.relationships.toExisting + landings.relationships.withinImport,
    };

  const rows = (items || []).length;
  const found = Number(itemsFound);
  return {
    // What the documents held, which is not the same number as what survived
    // dedupe — the reconciliation note explains any gap.
    itemsFound: Number.isFinite(found) && found > 0 ? Math.round(found) : rows,
    reviewRowCount: rows,
    vendorCount: vendorNames.size,
    newVendorCount: newVendorNames.length,
    newItemCount: landings.counts[LANDING.NEW_ITEM],
    priceUpdateCount: landings.counts[LANDING.UPDATE_PRICE],
    newOptionCount: landings.counts[LANDING.NEW_OPTION],
    skippedCount: landings.counts[LANDING.SKIP] + landings.counts[LANDING.REJECT],
    relatedToExisting: related.toExisting,
    relatedWithinImport: related.withinImport,
    relatedUnknownOrigin: related.unknownOrigin,
    relationshipCount: related.total,
    // True when every counted relationship knows which side of the import its
    // partner is on. False means the split would be a guess, so the header
    // reports one total instead of two invented buckets.
    originKnown: related.unknownOrigin === 0,
  };
}
